// Switching a converted skeleton's track the way a Rive state machine transitions between states: rive-spine's companion
// runtime (`runtime/riveTransitions.ts`, copied 2026-10-11 and again 2026-10-23 with an exit transition's mix, the
// triggers a running mix drops and Rive's signed scale mix: rive-spine's working tree that day, sha256 c3811bc3…37ff5;
// unchanged but for this header; the engine's copy since decision 72, the second copy decision 82).
//
// Rive (state_machine_instance.cpp): a transition with a duration mixes the two states linearly and runs to the end,
// so a condition that changes meanwhile (a boolean flipped back mid-fade) takes effect in the advance where the mix
// runs out, and that state starts at 0 (a state left before its end spills no time). An exit-time transition starts
// the next state in the advance where the animation ends, at the time that spilled over. Spine's setAnimation cuts
// into a running mix instead, and its queued entries start one update after their time (with the time made up).
//
// The first call on an AnimationState wraps its update (once) to make those switches on time; the track is then the
// helper's: queue nothing else on it with addAnimation.
import { MixBlend, ScaleXTimeline, ScaleYTimeline, type AnimationState, type Skeleton, type SkeletonData, type TrackEntry } from "@esotericsoftware/spine-pixi-v8";

interface Next {
  animation: string;
  loop: boolean;
  /** Seconds the exit-time transition mixes over (0: at once). */
  mix?: number;
}

interface Request extends Next {
  mix: number;
  then?: Next;
}

interface TrackPlan {
  /** A request waiting for the running mix to end. */
  pending?: Request;
  /** The state an exit-time transition goes to when `after` ends. */
  then?: Next & { after: TrackEntry };
}

const plans = new WeakMap<AnimationState, Map<number, TrackPlan>>();

/** Skeletons driven by `transition()`: their scale mixes as Rive mixes it (see mixScaleAsRive). */
const riveSkeletons = new WeakSet<SkeletonData>();
let scalePatched = false;

/**
 * Rive mixes a property linearly, signed (LinearAnimation::apply: value · mix + current · (1 - mix)). spine-core's scale
 * timelines bend a mix by sign (mixing in, the current scale's size with the key's sign; mixing out, the key's size
 * with the current sign), so a part flipped by negative scale (a coin turning over) flips back mid-mix. For skeletons
 * `transition()` drives, a scale timeline applied at less than full weight mixes as Rive's does; any other skeleton
 * keeps spine-core's way.
 */
function mixScaleAsRive(): void {
  if (scalePatched) return;
  scalePatched = true;
  for (const [Timeline, axis] of [[ScaleXTimeline, "scaleX"], [ScaleYTimeline, "scaleY"]] as const) {
    type Apply = (skeleton: Skeleton, lastTime: number, time: number, events: unknown, alpha: number, blend: MixBlend, direction: unknown) => void;
    const proto = Timeline.prototype as unknown as { apply: Apply; boneIndex: number; frames: ArrayLike<number>; getCurveValue(time: number): number };
    const apply = proto.apply;
    proto.apply = function (this: typeof proto, skeleton: Skeleton, lastTime: number, time: number, events: unknown, alpha: number, blend: MixBlend, direction: unknown): void {
      const bone = skeleton.bones[this.boneIndex]!;
      if (alpha >= 1 || blend === MixBlend.add || time < this.frames[0]! || !riveSkeletons.has(skeleton.data) || !bone.active) {
        apply.call(this, skeleton, lastTime, time, events, alpha, blend, direction);
        return;
      }
      const value = this.getCurveValue(time) * bone.data[axis];
      const from = blend === MixBlend.setup ? bone.data[axis] : bone[axis];
      bone[axis] = from + (value - from) * alpha;
    };
  }
}

/** A mix that has finished but is still attached (AnimationState drops it on the next update). */
function dropFinishedMix(entry: TrackEntry): void {
  const from = entry.mixingFrom;
  if (from && entry.mixTime >= entry.mixDuration && !from.mixingFrom) {
    entry.mixingFrom = null;
    from.mixingTo = null;
  }
}

function start(state: AnimationState, plan: TrackPlan, track: number, request: Request, time: number): TrackEntry {
  const current = state.getCurrent(track);
  if (current) dropFinishedMix(current);
  const entry = state.setAnimation(track, request.animation, request.loop);
  entry.mixDuration = request.mix;
  entry.trackTime = time;
  plan.then = request.then ? { ...request.then, after: entry } : undefined;
  return entry;
}

function install(state: AnimationState): Map<number, TrackPlan> {
  let map = plans.get(state);
  if (map) return map;
  riveSkeletons.add(state.data.skeletonData);
  mixScaleAsRive();
  const tracks = new Map<number, TrackPlan>();
  map = tracks;
  plans.set(state, tracks);
  const update = state.update.bind(state);
  state.update = (delta: number): void => {
    update(delta);
    for (const [track, plan] of tracks) {
      const current = state.getCurrent(track);
      // The running mix ran out in this update: the waiting state starts now, at 0.
      if (plan.pending && (!current?.mixingFrom || current.mixTime >= current.mixDuration)) {
        const request = plan.pending;
        plan.pending = undefined;
        start(state, plan, track, request, 0);
        continue;
      }
      // Exit time: the animation ended in this update; the next state starts at the time that spilled over.
      const then = plan.then;
      if (then && current === then.after && !current.loop && current.trackTime >= current.animationEnd - current.animationStart) {
        plan.then = undefined;
        start(state, plan, track, { ...then, mix: then.mix ?? 0 }, current.trackTime - (current.animationEnd - current.animationStart));
      }
    }
  };
  return map;
}

/**
 * Switches `track` to `animation` as a Rive transition of `mix` seconds does: at once when no mix is running, else in
 * the update where the running mix ends (only the latest request counts, and one back to the state being mixed in
 * cancels; `trigger`: a view-model trigger's transition, which a running mix drops instead, as Rive's state machine
 * does). `then`: the state an exit-time transition goes to when this animation ends (over `then.mix` seconds, as
 * that transition's duration; the animation that ended holds its last frame meanwhile). Returns the entry, or
 * undefined while the request waits (or when the track is in that state already).
 */
export function transition(state: AnimationState, track: number, animation: string, mix: number, options: { loop?: boolean; then?: { animation: string; loop?: boolean; mix?: number }; trigger?: boolean } = {}): TrackEntry | undefined {
  const tracks = install(state);
  const plan = tracks.get(track) ?? {};
  tracks.set(track, plan);
  const request: Request = { animation, mix, loop: options.loop ?? false, then: options.then ? { animation: options.then.animation, loop: options.then.loop ?? false, mix: options.then.mix ?? 0 } : undefined };
  const current = state.getCurrent(track);
  if (current?.mixingFrom && current.mixTime < current.mixDuration) {
    // A trigger fired meanwhile expires with the frame (StateMachineLayerInstance::updateState); a boolean holds.
    if (options.trigger) return undefined;
    plan.pending = current.animation?.name === animation ? undefined : request;
    return undefined;
  }
  plan.pending = undefined;
  if (current?.animation?.name === animation) return undefined;
  return start(state, plan, track, request, 0);
}
